/**
 * Universal function to get the active workspace from a list of workspaces.
 *
 * This function handles different workspace schemas by using a generic type that
 * accepts partial properties. It implements a fallback mechanism:
 * - First tries to find a workspace with `active: true`
 * - If no active workspace is found and there's only one workspace, uses that one
 *
 * @template T - Generic type extending workspace properties (active, uniqueCode, createdAt, company)
 * @param workspaces - Array of workspace objects or undefined
 * @returns The active workspace object or undefined if none found
 */
export const getActiveWorkspaceUniversal = <
  T extends Partial<{
    active: boolean;
    uniqueCode: string;
  }>
>(
  workspaces: T[] | undefined
): T | undefined => {
  if (!workspaces) return undefined;

  // First, try to find a workspace marked as active
  let activeWorkspace = workspaces.find((workspace) => workspace.active);

  // Fallbacks when no active workspace is found
  // 1) If there is exactly one workspace, use that one
  // 2) If there are multiple, fallback to the first deterministically
  if (!activeWorkspace && workspaces.length > 0) {
    activeWorkspace = workspaces[0];
  }

  return activeWorkspace;
};
